import type { AssignedForm } from "../../types";
import {
  formatDateShort,
  formatDueDate,
  formatPeriodRange,
} from "../../utils/format";
import { IcoCheck, IcoChevronRight, IcoClock } from "../icons";

// Grace window closes 24 hours after the period boundary. For a grace-period
// card, `periodEnd` IS the boundary, so close time = periodEnd + 24h.
const GRACE_WINDOW_MS = 24 * 60 * 60 * 1000;

function formatGraceCloses(periodEnd: Date | string | null): string {
  if (!periodEnd) return "";
  const closeAt = new Date(new Date(periodEnd).getTime() + GRACE_WINDOW_MS);
  const now = new Date();
  const diffHours = (closeAt.getTime() - now.getTime()) / (1000 * 60 * 60);
  const timeStr = closeAt.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (diffHours <= 0) return "Closing now";
  const closeDay = new Date(
    closeAt.getFullYear(),
    closeAt.getMonth(),
    closeAt.getDate(),
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (closeDay.getTime() === today.getTime()) return `Closes today ${timeStr}`;
  return `Closes tomorrow ${timeStr}`;
}

type PendingCheckInCardProps = {
  forms: AssignedForm[];
};

export function PendingCheckInCard({ forms }: PendingCheckInCardProps) {
  if (forms.length === 0) return null;

  // Separate pending and submitted forms
  const pendingForms = forms.filter((f) => f.status !== "submitted");
  const submittedForms = forms.filter((f) => f.status === "submitted");

  // If all forms are submitted, show the submitted state
  if (pendingForms.length === 0 && submittedForms.length > 0) {
    return <SubmittedFormsCard forms={submittedForms} />;
  }

  // Show the most urgent pending form
  const urgentForm = pendingForms[0];
  if (!urgentForm) return null;

  const isGrace = urgentForm.isGracePeriod;
  const dueText = isGrace
    ? formatGraceCloses(urgentForm.periodEnd ?? null)
    : formatDueDate(urgentForm.periodEnd ?? null);
  const isOverdue = !isGrace && dueText === "Overdue!";

  // Single pending form: entire card is clickable
  if (pendingForms.length === 1 && submittedForms.length === 0) {
    return (
      <a
        href={`/orgs/${urgentForm.orgId}/forms/${urgentForm.id}`}
        class={`group block rounded-xl border p-5 sm:p-6 transition-all duration-200 ${
          isGrace
            ? "border-amber-200 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-950/20 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm"
            : isOverdue
              ? "border-red-200 dark:border-red-800 bg-red-50/80 dark:bg-red-950/20 hover:border-red-300 dark:hover:border-red-700 hover:shadow-sm"
              : "border-[var(--brand-200)] bg-[var(--brand-50)]/60 dark:bg-[var(--brand-950)]/20 hover:border-[var(--brand-300)] hover:shadow-sm"
        }`}
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            {/* Status line */}
            <div
              class={`flex items-center gap-1.5 mb-3 ${
                isGrace
                  ? "text-amber-700 dark:text-amber-300"
                  : isOverdue
                    ? "text-red-600 dark:text-red-400"
                    : "text-[var(--brand-600)]"
              }`}
            >
              <IcoClock class="size-3.5 flex-shrink-0" />
              <span class="text-xs font-semibold uppercase tracking-wider">
                {isGrace
                  ? "Late — last week's check-in"
                  : isOverdue
                    ? "Overdue"
                    : "Pending check-in"}
              </span>
            </div>

            {/* Title */}
            <h3 class="text-lg font-semibold text-text leading-snug mb-1">
              {urgentForm.title || "Untitled form"}
            </h3>

            {/* Period and due date */}
            <p class="text-sm text-text-muted leading-relaxed">
              {formatPeriodRange(
                urgentForm.periodStart ?? null,
                urgentForm.periodEnd ?? null,
              )}
              {dueText && (
                <>
                  <span class="mx-1.5 text-text-subtle">&middot;</span>
                  <span
                    class={
                      isGrace
                        ? "text-amber-700 dark:text-amber-300 font-medium"
                        : isOverdue
                          ? "text-red-600 dark:text-red-400 font-medium"
                          : ""
                    }
                  >
                    {dueText}
                  </span>
                </>
              )}
            </p>
          </div>

          {/* CTA */}
          <div class="flex-shrink-0 self-center">
            <span
              class={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                isGrace
                  ? "bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                  : isOverdue
                    ? "bg-red-600 hover:bg-red-700 text-white shadow-sm"
                    : "bg-[var(--brand-600)] group-hover:bg-[var(--brand-700)] text-white shadow-sm"
              }`}
            >
              {isGrace ? "Submit now" : "Start"}
              <IcoChevronRight class="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </a>
    );
  }

  // Multiple forms: show pending list + submitted list
  return (
    <div class="flex flex-col gap-4">
      <div
        class={`rounded-xl border p-5 sm:p-6 ${
          isOverdue
            ? "border-red-200 dark:border-red-800 bg-red-50/80 dark:bg-red-950/20"
            : "border-[var(--brand-200)] bg-[var(--brand-50)]/60 dark:bg-[var(--brand-950)]/20"
        }`}
      >
        <div
          class={`flex items-center gap-1.5 mb-4 ${
            isOverdue
              ? "text-red-600 dark:text-red-400"
              : "text-[var(--brand-600)]"
          }`}
        >
          <IcoClock class="size-3.5" />
          <span class="text-xs font-semibold uppercase tracking-wider">
            {pendingForms.length} Pending check-in
            {pendingForms.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div class="flex flex-col gap-2">
          {pendingForms.map((form) => {
            const formIsGrace = form.isGracePeriod;
            const formDueText = formIsGrace
              ? formatGraceCloses(form.periodEnd ?? null)
              : formatDueDate(form.periodEnd ?? null);
            const formIsOverdue = !formIsGrace && formDueText === "Overdue!";

            return (
              <a
                key={form.id}
                href={`/orgs/${form.orgId}/forms/${form.id}`}
                class={`group flex items-center justify-between p-3.5 rounded-lg transition-all duration-150 ${
                  formIsGrace
                    ? "bg-amber-100/60 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                    : formIsOverdue
                      ? "bg-red-100/60 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30"
                      : "bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10"
                }`}
              >
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <h3 class="font-medium text-text text-sm leading-snug">
                      {form.title || "Untitled form"}
                    </h3>
                    {formIsGrace && (
                      <span class="inline-flex items-center rounded-full bg-amber-200 dark:bg-amber-800/50 text-amber-900 dark:text-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                        Late
                      </span>
                    )}
                  </div>
                  <p class="text-xs text-text-muted mt-0.5">
                    {formatPeriodRange(
                      form.periodStart ?? null,
                      form.periodEnd ?? null,
                    )}
                  </p>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0 ml-4">
                  <span
                    class={`text-xs font-medium ${
                      formIsGrace
                        ? "text-amber-700 dark:text-amber-300"
                        : formIsOverdue
                          ? "text-red-600 dark:text-red-400"
                          : "text-text-muted"
                    }`}
                  >
                    {formDueText}
                  </span>
                  <IcoChevronRight class="size-4 text-text-subtle group-hover:text-text-muted transition-colors" />
                </div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Show submitted forms below */}
      {submittedForms.length > 0 && (
        <SubmittedFormsCard forms={submittedForms} />
      )}
    </div>
  );
}

/**
 * Card shown when all forms for the current period have been submitted.
 */
function SubmittedFormsCard({ forms }: { forms: AssignedForm[] }) {
  const form = forms[0];
  if (!form) return null;

  const submittedDate = form.submittedAt
    ? new Date(form.submittedAt).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : null;

  // Single submitted form
  if (forms.length === 1) {
    return (
      <a
        href={`/orgs/${form.orgId}/forms/${form.id}/submissions/${form.submissionId}`}
        class="group block rounded-xl border border-green-200 dark:border-green-800/60 bg-green-50/60 dark:bg-green-950/20 p-5 sm:p-6 hover:border-green-300 dark:hover:border-green-700 hover:shadow-sm transition-all duration-200"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            {/* Status line */}
            <div class="flex items-center gap-1.5 mb-3 text-green-600 dark:text-green-400">
              <IcoCheck class="size-3.5 flex-shrink-0" />
              <span class="text-xs font-semibold uppercase tracking-wider">
                Submitted
              </span>
            </div>

            {/* Title */}
            <h3 class="text-lg font-semibold text-text leading-snug mb-1">
              {form.title || "Untitled form"}
            </h3>

            {/* Period and submitted date */}
            <p class="text-sm text-text-muted leading-relaxed">
              {formatPeriodRange(
                form.periodStart ?? null,
                form.periodEnd ?? null,
              )}
              {submittedDate && (
                <>
                  <span class="mx-1.5 text-text-subtle">&middot;</span>
                  <span class="text-green-600 dark:text-green-400">
                    Submitted {submittedDate}
                  </span>
                </>
              )}
            </p>
            {form.isRecurring && form.periodEnd && (
              <p class="text-xs text-text-subtle mt-1">
                Next check-in opens {formatDateShort(new Date(form.periodEnd))}
              </p>
            )}
          </div>

          {/* CTA */}
          <div class="flex-shrink-0 self-center">
            <span class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-surface border border-border text-text-secondary group-hover:bg-surface-hover transition-all duration-200">
              View
              <IcoChevronRight class="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </a>
    );
  }

  // Multiple submitted forms
  return (
    <div class="rounded-xl border border-green-200 dark:border-green-800/60 bg-green-50/60 dark:bg-green-950/20 p-5 sm:p-6">
      <div class="flex items-center gap-1.5 mb-4 text-green-600 dark:text-green-400">
        <IcoCheck class="size-3.5" />
        <span class="text-xs font-semibold uppercase tracking-wider">
          {forms.length} check-ins submitted
        </span>
      </div>

      <div class="flex flex-col gap-2">
        {forms.map((f) => {
          const fSubmittedDate = f.submittedAt
            ? new Date(f.submittedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })
            : null;

          return (
            <a
              key={f.id}
              href={`/orgs/${f.orgId}/forms/${f.id}/submissions/${f.submissionId}`}
              class="group flex items-center justify-between p-3.5 rounded-lg bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 transition-all duration-150"
            >
              <div class="min-w-0">
                <h3 class="font-medium text-text text-sm leading-snug">
                  {f.title || "Untitled"}
                </h3>
                <p class="text-xs text-text-muted mt-0.5">
                  {formatPeriodRange(
                    f.periodStart ?? null,
                    f.periodEnd ?? null,
                  )}
                  {f.isRecurring && f.periodEnd && (
                    <>
                      <span class="mx-1 text-text-subtle">&middot;</span>
                      <span class="text-text-subtle">
                        Next opens {formatDateShort(new Date(f.periodEnd))}
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0 ml-4">
                <span class="text-xs text-green-600 dark:text-green-400 font-medium">
                  {fSubmittedDate}
                </span>
                <IcoChevronRight class="size-4 text-text-subtle group-hover:text-text-muted transition-colors" />
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
